// src/components/routing/NavMenuLink.tsx
import React from 'react';
import { NavLink, NavLinkProps } from 'react-router';
import classNames from 'classnames';
import { NavMenuItemType } from "../../views/nav-menu/NavMenuSubmenuListItem";
import { useObservableBadgeValue } from "../../hooks/observable/useObservableBadgeValue";
import { formatMenuItemUrl } from "../../routing/route-utils";

interface NavMenuLinkProps extends Omit<NavLinkProps, 'className' | 'to'> {
  /** Additional class names to apply */
  className?: string | ((props: { isActive: boolean }) => string);
  /** Class name to apply when link is active */
  activeClassName?: string;
  /** Menu item containing navigation data */
  menuItem: NavMenuItemType;
  /** Optional destination - defaults to menuItem.path or '#' */
  //to?: string;
}

/**
 * Extended NavLink component that simplifies active state className handling.
 * Wraps react-router NavLink with consistent active state behavior.
 */
const NavMenuLink: React.FC<NavMenuLinkProps> = ({
  className,
  activeClassName = 'active',
  menuItem,
  children,
  ...rest
}) => {
  const destination = formatMenuItemUrl(menuItem, useObservableBadgeValue(menuItem));

  if (menuItem.external) {
    const resolvedClassName = typeof className === 'function'
      ? className({ isActive: false })
      : classNames(className);

    const content = typeof children === 'function'
      ? children({ isActive: false, isPending: false, isTransitioning: false })
      : children;

    const { onClick, style, id } = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;

    return (
      <a
        href={destination}
        target="_blank"
        rel="noopener noreferrer"
        className={resolvedClassName}
        onClick={onClick}
        style={style}
        id={id}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      className={({ isActive }) => {
        if (typeof className === 'function') {
          return className({ isActive });
        }
        return classNames(className, { [activeClassName]: isActive });
      }}
      to={destination}
      {...rest}
    >
      {children}
    </NavLink>
  );
};

export { NavMenuLink };